/**
 * The full-bleed screen the original uses for everything that is not a menu
 * board: the briefings, the assignment and roster sheets, the debriefing, the
 * Realism Panel, the list pickers and the enlistment.
 *
 * All nine captures share one header, and it is the same construction the
 * cockpit lane found in the dash: **one piece of art drawn twice, mirrored**.
 * `PARTS.BMP` frame 11 (the 56×21 stack of wing bars) sits at x=0 flipped and
 * at x=264 upright; frame 12 (a 64×4 bar) at x=53 and x=204; and a 24×17
 * emblem at x=48 and x=256 — frame 14 for an Allied screen, 15 for a German
 * one, 13 (a plain boss) where the screen has no side, which is what
 * `realism-panel.png` shows. Frames 13 and 15 were template-matched at **zero**
 * mismatched palette indices, and so, now, is 14: its shape always matched but
 * its baked colours did not, because the bake paired `PARTS.BMP` with a
 * palette whose low block differs from the one this screen loads (the same
 * class of mistake `docs/engineering/lessons.md` records for `DASH_B`). The
 * bake now pins the sheet to `PS.PAL` — where indices 201–207 are the
 * roundel's blue ramp rather than `TITLE.PAL`'s orange — so the shell no
 * longer re-inks it on load.
 *
 * The title is FONT2 in flat black with its ink on rows 4-12, and the centring
 * rule below reproduces the measured left edge of **all nine** captured titles
 * exactly.
 */

import { boardFieldIndex, type DrawOp, fill, type FontId, LOGICAL_WIDTH, type TextStyle } from "./draw-ops.ts";
import { PANEL_FILL } from "./plate-chrome.ts";
import { type FontAtlas, measureText } from "./text-metrics.ts";

export const PANEL_SHEET = "sprites/PARTS.BMP";
export const PANEL_TITLE_FONT: FontId = "FONT2";
/**
 * The panel title is a three-pass emboss the Main Menu's banner does not use:
 * the glyph in black with a 227 copy one row above and a 235 copy one row
 * below. `TextStyle`'s two-copy model reproduces the below-copy exactly and
 * puts the 227 on the glyph's own top row rather than one row above it — a
 * one-pixel difference on the top edge of each stroke, and the only place the
 * panel header departs from the capture.
 */
export const PANEL_TITLE_STYLE: TextStyle = { body: 0, shadow: 227, highlight: 235 };
export const PANEL_HEADER_HEIGHT = 17;
/** Top and bottom edge of the header band. */
const HEADER_EDGE = 240;

export type PanelEmblemId = "roundel" | "cross" | "none";

const EMBLEM_FRAME: Readonly<Record<PanelEmblemId, number>> = { roundel: 14, cross: 15, none: 13 };

const WING_FRAME = 11;
const BAR_FRAME = 12;

/**
 * The header band's own ramp. It is not a new gradient: bucketing
 * `realism-panel.png`'s rows 1-15 by `2x − 3y` collapses 411 samples onto a
 * single curve with only 105 ambiguous buckets — the *same* dx/dy = 1.5 shear,
 * the same 456 period and the same 226…237 amplitude as the board field, at a
 * different phase. `boardFieldIndex`'s constant is 415; the header's is 104.
 */
const HEADER_PHASE = 104;

export function headerFieldIndex(x: number, y: number): number {
  return boardFieldIndex(x, y, HEADER_PHASE);
}

function headerBandOps(): DrawOp[] {
  const ops: DrawOp[] = [fill({ x: 0, y: 0, w: LOGICAL_WIDTH, h: 1 }, HEADER_EDGE)];
  for (let y = 1; y < PANEL_HEADER_HEIGHT - 1; y += 1) {
    let runStart = 0;
    let runColor = headerFieldIndex(0, y);
    for (let x = 1; x <= LOGICAL_WIDTH; x += 1) {
      const color = x === LOGICAL_WIDTH ? -1 : headerFieldIndex(x, y);
      if (color === runColor) continue;
      ops.push(fill({ x: runStart, y, w: x - runStart, h: 1 }, runColor));
      runStart = x;
      runColor = color;
    }
  }
  ops.push(fill({ x: 27, y: PANEL_HEADER_HEIGHT - 1, w: 266, h: 1 }, HEADER_EDGE));
  return ops;
}

function sprite(frame: number, x: number, y: number, flipH = false): DrawOp {
  return { kind: "sprite", sheet: PANEL_SHEET, frame, x, y, flipH };
}

/** Left edge of a panel title. Reproduces all nine measured captures: `161 − ⌊advance/2⌋`. */
export function panelTitleX(font: FontAtlas, title: string): number {
  return 161 - Math.floor(measureText(font, title) / 2);
}

export function panelHeaderOps(title: string, emblem: PanelEmblemId, font: FontAtlas): DrawOp[] {
  const frame = EMBLEM_FRAME[emblem];
  return [
    ...headerBandOps(),
    sprite(WING_FRAME, 0, 0, true),
    sprite(WING_FRAME, 264, 0),
    sprite(BAR_FRAME, 53, 17, true),
    sprite(BAR_FRAME, 204, 17),
    sprite(frame, 48, 0),
    sprite(frame, 256, 0),
    { kind: "text", font: PANEL_TITLE_FONT, text: title, x: panelTitleX(font, title), y: 1, style: PANEL_TITLE_STYLE },
  ];
}

/** The whole frame: the flat body, or a background screen, with the header over it. */
export function panelOps(title: string, emblem: PanelEmblemId, font: FontAtlas, background?: string): DrawOp[] {
  const base: DrawOp[] = background
    ? [{ kind: "image", asset: background, x: 0, y: 0 }]
    : [fill({ x: 0, y: PANEL_HEADER_HEIGHT, w: LOGICAL_WIDTH, h: 200 - PANEL_HEADER_HEIGHT }, PANEL_FILL)];
  return [...base, ...panelHeaderOps(title, emblem, font)];
}
